import { ReactNode, useState, useEffect } from 'react'
import { Head, usePage } from '@inertiajs/react'
import { Navigation } from '@/components/Navigation'
import { Footer } from '@/components/Footer'
import { Banner } from '@/components/Banner'
import { EditableContent } from '@/components/EditableContent'
import { BadgeUnlockModal } from '@/components/BadgeUnlockModal'
import { ChatbotWidget } from '@/components/ChatbotWidget'
import { WeeklyQuizNudgeModal } from '@/components/WeeklyQuizNudgeModal'
import type { EditableContent as EditableContentType, PageProps, NewlyAwardedBadge, PendingQuiz } from '@/types'

interface MainLayoutProps {
  children: ReactNode
  title: string
  banner?: {
    content: string
    cta?: { label: string; href: string }
    variant?: 'info' | 'success' | 'warning'
    id?: string
  }
  editableFooter?: EditableContentType | null
}

interface SharedProps extends PageProps {
  editable_footer?: EditableContentType | null
  newly_awarded_badges?: NewlyAwardedBadge[] | null
  pending_quiz?: PendingQuiz | null
}

export function MainLayout({ children, title, banner, editableFooter }: MainLayoutProps) {
  // Récupère le footer et les données partagées
  const { editable_footer: sharedFooter, newly_awarded_badges, pending_quiz } = usePage<SharedProps>().props
  const { url } = usePage()
  const footerContent = editableFooter ?? sharedFooter

  // State pour afficher les badges un par un
  const [badgesToShow, setBadgesToShow] = useState<NewlyAwardedBadge[]>([])
  const [currentBadgeIndex, setCurrentBadgeIndex] = useState(0)

  // Initialiser les badges à afficher quand on reçoit les données
  useEffect(() => {
    if (newly_awarded_badges && newly_awarded_badges.length > 0) {
      setBadgesToShow(newly_awarded_badges)
      setCurrentBadgeIndex(0)
    }
  }, [newly_awarded_badges])

  const handleCloseBadgeModal = () => {
    if (currentBadgeIndex < badgesToShow.length - 1) {
      // Passer au badge suivant
      setCurrentBadgeIndex(currentBadgeIndex + 1)
    } else {
      // Fermer le modal
      setBadgesToShow([])
      setCurrentBadgeIndex(0)
      // Dispatch event to notify that all badge modals are closed
      window.dispatchEvent(new CustomEvent('badgeModalsComplete'))
    }
  }

  const currentBadge = badgesToShow[currentBadgeIndex]

  // --- Quiz nudge modal logic ---
  const [showQuizNudge, setShowQuizNudge] = useState(false)

  useEffect(() => {
    // Don't show if no pending quiz, or if on /defis page
    if (!pending_quiz || url.startsWith('/defis')) return

    // Check localStorage: already dismissed today for this quiz?
    const dismissKey = `quiz_nudge_dismissed_${pending_quiz.id}`
    const dismissedDate = localStorage.getItem(dismissKey)
    const today = new Date().toISOString().slice(0, 10)
    if (dismissedDate === today) return

    // If badge modals are active, wait for them to complete
    if (newly_awarded_badges && newly_awarded_badges.length > 0) {
      const handleBadgesComplete = () => {
        const timer = setTimeout(() => setShowQuizNudge(true), 500)
        return () => clearTimeout(timer)
      }
      window.addEventListener('badgeModalsComplete', handleBadgesComplete, { once: true })
      return () => window.removeEventListener('badgeModalsComplete', handleBadgesComplete)
    }

    // No badge modals: show after 800ms delay
    const timer = setTimeout(() => setShowQuizNudge(true), 800)
    return () => clearTimeout(timer)
  }, [pending_quiz, url, newly_awarded_badges])

  const handleDismissQuizNudge = () => {
    setShowQuizNudge(false)
    if (pending_quiz) {
      localStorage.setItem(`quiz_nudge_dismissed_${pending_quiz.id}`, new Date().toISOString().slice(0, 10))
    }
  }

  return (
    <>
      <Head title={`${title} - Tessan Community`} />
      <div className="min-h-screen bg-bg-page flex flex-col">
        <Navigation />
        {banner && (
          <Banner
            content={banner.content}
            cta={banner.cta}
            variant={banner.variant}
            id={banner.id}
          />
        )}
        <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-8">
          {children}
        </main>
        {/* Footer éditable ou footer par défaut */}
        {footerContent ? (
          <EditableContent
            content={footerContent}
            fallback={<Footer />}
          />
        ) : (
          <Footer />
        )}
      </div>

      {/* Modal de célébration des badges */}
      {currentBadge && (
        <BadgeUnlockModal
          badge={currentBadge}
          onClose={handleCloseBadgeModal}
        />
      )}

      {/* Chatbot widget */}
      <ChatbotWidget />

      {/* Modal nudge quiz hebdomadaire */}
      {!currentBadge && showQuizNudge && pending_quiz && (
        <WeeklyQuizNudgeModal
          quiz={pending_quiz}
          onClose={handleDismissQuizNudge}
        />
      )}
    </>
  )
}
